/**
 * "Delete the drops outside the window" (user, 19/10/2026: "I want a button
 * to delete all slots who is outside the window") — on the right of the
 * Drop slots footer, only while a drop is outside the window today and the
 * container does not lift it. One confirm names the drops and the ones kept;
 * drops before the window opens get "Start at window open" instead (deleting
 * them would pull the next drops into their place). It edits the draft only:
 * Save keeps it, Cancel undoes it.
 * For Media Devoted | Created By Ciro Autiero
 */
import { ComponentProps, FC, useState } from 'react';
import { Moon, Trash2 } from 'lucide-react';

import { Button, ConfirmDialog } from '@/components/ui';

import type { OutsideRemovalModel } from '../../hooks/use-outside-removal';
import { positionsLabel } from './drop-positions';
import { OutsideRemovalBody } from './OutsideRemovalBody';

interface Props {
  model: OutsideRemovalModel | null;
  /** Draft positions in the order the list reads — the dialog names drops by their row number. */
  timeOrder: number[];
  onRemove: (indexes: number[]) => void;
  /** Start mode → when the window opens. */
  onStartAtOpen: () => void;
}

const drops = (n: number) => `${n} drop${n === 1 ? '' : 's'}`;

export const DeleteOutsideButton: FC<Props> = ({ model, timeOrder, onRemove, onStartAtOpen }) => {
  const [open, setOpen] = useState(false);
  if (!model || model.outsideToday === 0) return null;
  const close = () => setOpen(false);
  const n = model.remove.length;
  const run = (act: () => void) => () => { act(); close(); };
  return (
    <>
      <Button variant="outline" size="sm" className="ml-auto border-warning/40 text-warning hover:bg-warning/10" onClick={() => setOpen(true)}>
        {n > 0 ? <Trash2 className="mr-1 h-3.5 w-3.5" aria-hidden /> : <Moon className="mr-1 h-3.5 w-3.5" aria-hidden />}
        {n > 0 ? `Delete ${n} outside the window` : `${model.outsideToday} outside the window`}
      </Button>
      <ConfirmDialog
        open={open}
        title={titleOf(model)}
        description={<OutsideRemovalBody model={model} positions={positionsLabel(model.remove, timeOrder)} />}
        widthClass="max-w-lg"
        onCancel={close}
        {...actionsOf(model, { remove: run(() => onRemove(model.remove)), fix: run(onStartAtOpen), close })}
      />
    </>
  );
};

function titleOf(model: OutsideRemovalModel): string {
  if (model.remove.length > 0) return `Delete ${drops(model.remove.length)} outside the window?`;
  if (model.leading > 0) return `${drops(model.leading)} before the window opens`;
  return `${drops(model.outsideToday)} outside the window`;
}

type Actions = Pick<ComponentProps<typeof ConfirmDialog>, 'confirmLabel' | 'destructive' | 'onConfirm' | 'secondaryAction' | 'hideCancel'>;

/** Delete (with the start fix beside it), the start fix alone, or nothing to do but read why. */
function actionsOf(model: OutsideRemovalModel, act: { remove: () => void; fix: () => void; close: () => void }): Actions {
  const fix = model.startFix ? { label: 'Start at window open', onClick: act.fix } : undefined;
  if (model.remove.length > 0) return { confirmLabel: `Delete ${model.remove.length}`, destructive: true, onConfirm: act.remove, secondaryAction: fix };
  if (fix) return { confirmLabel: fix.label, onConfirm: fix.onClick };
  return { confirmLabel: 'OK', onConfirm: act.close, hideCancel: true };
}
